import { UPDropdown } from 'components/dropdown';
import UPInput from 'components/input';
import { useNoShowReasonOptions } from 'hooks/useNoShowReasonOptions';
import { toNumber } from 'lodash';
import { useEffect } from 'react';
import { Controller, UseFormReturn, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { costDeclarationSelectItemsForCompanySelector } from 'store/selectors/cost-declaration-selectors';
import {
  CONTRACTS__COST_DELCARATION,
  CONTRACTS__COST_DELCARATION_AMOUNT,
  CONTRACTS__COST_DELCARATION_VALUE,
  CONTRACTS__NO_SHOW,
  CONTRACTS__REASON_FOR_NO_SHOW,
  CONTRACTS__SELECT_REASON,
  GENERAL__SELECT,
  WAGE_COMPONENTS__COST_DECLARATION_ADD,
} from 'translations/constants';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import {
  AbsenceCostColumnWrapper,
  AbsenceCostFieldWrapper,
  AbsenceCostRow,
  AddBreakContainer,
  CostDeclarationDataRow,
  CostDeclarationDeleteButton,
  CostDeclarationTypeWrapper,
  CostDeclarationValueWrapper,
  LinkButton,
  SubSectionTitle,
} from '../styles';
import { ContractDetailPanelData, DetailPanelFormValues } from '../types';

interface AbsenceCostDeclarationSectionProps {
  form: UseFormReturn<DetailPanelFormValues>;
  noShowReasonDetail: ContractDetailPanelData['noShowReasonDetail'];
  /** True after the backend rejected the chosen reason as no longer active; the options are then reloaded. */
  isNoShowReasonUnavailable: boolean;
}

const AbsenceCostDeclarationSection = (props: AbsenceCostDeclarationSectionProps) => {
  const { form, noShowReasonDetail, isNoShowReasonUnavailable } = props;
  const { t } = useTranslation();
  const { items: reasons, reload: reloadReasons } = useNoShowReasonOptions(noShowReasonDetail);

  useEffect(() => {
    if (isNoShowReasonUnavailable) reloadReasons();
  }, [isNoShowReasonUnavailable, reloadReasons]);
  const costDeclarationOptions = useAppSelector(costDeclarationSelectItemsForCompanySelector);

  const watchedRows = form.watch('costDeclarationRows');

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'costDeclarationRows',
  });

  return (
    <AbsenceCostRow>
      {/* Absence reason */}
      <AbsenceCostFieldWrapper>
        <SubSectionTitle>{t(CONTRACTS__NO_SHOW)}</SubSectionTitle>
        <Controller
          name="reasonForNoShow"
          control={form.control}
          render={({ field }) => (
            <UPDropdown
              value={field.value}
              onChange={field.onChange}
              placeholder={t(CONTRACTS__SELECT_REASON)}
              items={reasons}
              label={t(CONTRACTS__REASON_FOR_NO_SHOW)}
              id="detail-panel-absence-reason"
              testId="noshow-reason-select"
              isFilter
            />
          )}
        />
      </AbsenceCostFieldWrapper>

      {/* Cost declarations */}
      <AbsenceCostColumnWrapper>
        <SubSectionTitle>{t(CONTRACTS__COST_DELCARATION)}</SubSectionTitle>
        {fields.map((fieldItem, index) => {
          const hasSelection = !!watchedRows[index]?.costDeclarationId;
          return (
            <CostDeclarationDataRow key={fieldItem.id}>
              <CostDeclarationTypeWrapper>
                <Controller
                  name={`costDeclarationRows.${index}.costDeclarationId`}
                  control={form.control}
                  render={({ field }) => (
                    <UPDropdown
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t(GENERAL__SELECT)}
                      items={costDeclarationOptions}
                      label={t(CONTRACTS__COST_DELCARATION)}
                      id={`detail-panel-cost-decl-${index}`}
                      isFilter={index === 0 && fields.length === 1}
                    />
                  )}
                />
              </CostDeclarationTypeWrapper>
              {hasSelection && (
                <>
                  <CostDeclarationValueWrapper>
                    <Controller
                      name={`costDeclarationRows.${index}.amount`}
                      control={form.control}
                      render={({ field }) => (
                        <UPInput
                          value={field.value > 0 ? field.value : ''}
                          onChange={v => field.onChange(toNumber(v))}
                          placeholder="0"
                          label={t(CONTRACTS__COST_DELCARATION_AMOUNT)}
                          type="number"
                          step={0.0001}
                          inputClassName="white-background"
                          id={`detail-panel-cost-decl-amount-${index}`}
                        />
                      )}
                    />
                  </CostDeclarationValueWrapper>
                  <CostDeclarationValueWrapper>
                    <Controller
                      name={`costDeclarationRows.${index}.quantity`}
                      control={form.control}
                      render={({ field }) => (
                        <UPInput
                          value={field.value > 0 ? field.value : ''}
                          onChange={v => field.onChange(toNumber(v))}
                          placeholder="€ 0"
                          label={`${t(CONTRACTS__COST_DELCARATION_VALUE)} (€)`}
                          type="number"
                          step={0.0001}
                          inputClassName="white-background"
                          id={`detail-panel-cost-decl-quantity-${index}`}
                        />
                      )}
                    />
                  </CostDeclarationValueWrapper>
                </>
              )}
              {index > 0 && (
                <CostDeclarationDeleteButton
                  type="button"
                  onClick={() => remove(index)}
                  id={`detail-panel-cost-decl-delete-${index}`}
                >
                  <CloseIcon fill="#303c42" />
                </CostDeclarationDeleteButton>
              )}
            </CostDeclarationDataRow>
          );
        })}
        {fields.length < costDeclarationOptions.length && (
          <AddBreakContainer>
            <LinkButton type="button" onClick={() => append({ costDeclarationId: '', amount: 0, quantity: 0 })}>
              {t(WAGE_COMPONENTS__COST_DECLARATION_ADD)}
            </LinkButton>
          </AddBreakContainer>
        )}
      </AbsenceCostColumnWrapper>
    </AbsenceCostRow>
  );
};

export default AbsenceCostDeclarationSection;
